import React, { useEffect, useState } from "react";

import { createSelector } from "@reduxjs/toolkit";
import { useUpdateApplicationMutation, getBorApplication } from "BrokerOfRecord/api";
import classnames from "classnames";
import { useSelector, useDispatch } from "react-redux";
import { reduxForm, SubmissionError, resetSection, formValueSelector } from "redux-form";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import focusFirstError from "Utils/focusFirstError";

import { Collapse } from "Cobalt/Collapse";
import { Title, Subtitle } from "Cobalt/Header";
import { Modal, ModalBody, ModalHeader } from "Cobalt/Modal";
import BodyText from "Common/BodyText";
import Button from "Common/Button";
import { Row, Col } from "Common/Grid";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";

import {
  MedicalInsuranceProvider,
  OtherMedicalInsuranceProvider,
  MedicalGroupIdNumber,
  ContactName,
  ContactTitle,
  Email,
  Address1,
  City,
  State,
  Zip,
  PhoneNumber,
} from "./CommonQuestions";
import { LetterContent, letterPreviewPropsFromBorApplication } from "./LetterPreview";

import makeBem from "Utils/makeBem";

const bem = makeBem("EditBrokerOfRecordLetterModal");

const CONFIRMATION_MODAL_FORM = "confirmation_modal";
const withReduxForm = reduxForm({
  form: CONFIRMATION_MODAL_FORM,
  destroyOnUnmount: false,
  enableReinitialize: true,
  onSubmit: async (values, _dispatch, props) => {
    try {
      await props.updateBorApplication(values).unwrap();
    } catch (e) {
      const {
        data: { error, fieldErrors },
      } = e;
      throw new SubmissionError({
        _error: error,
        ...fieldErrors,
      });
    }
  },
  onSubmitFail: (errors) => {
    focusFirstError(errors, ".");
  },
  onSubmitSuccess: (_values, _dispatch, { setFormOpen, setShowSuccessMessage }) => {
    setShowSuccessMessage(true);
    setFormOpen(false);
  },
});

const FormFields = () => {
  const formSelector = formValueSelector(CONFIRMATION_MODAL_FORM);
  const borMedicalInsuranceCompany = useSelector((state) => formSelector(state, "borMedicalInsuranceCompany"));

  const dispatch = useDispatch();
  return (
    <>
      <Subtitle py={3}>Company Address1</Subtitle>
      <Row>
        <Col sm={3}>
          <Row>
            <Address1 />
          </Row>
        </Col>
        <Col sm={3}>
          <Row>
            <City />
          </Row>
        </Col>
        <Col sm={3}>
          <Row>
            <State />
          </Row>
        </Col>
        <Col sm={3}>
          <Row>
            <Zip />
          </Row>
        </Col>
      </Row>
      <Subtitle py={3}>Authorized Representative</Subtitle>
      <Row>
        <Col sm={6}>
          <Row>
            <ContactName />
          </Row>
        </Col>
        <Col sm={6}>
          <Row>
            <ContactTitle />
          </Row>
        </Col>
        <Col sm={6}>
          <Row>
            <Email />
          </Row>
        </Col>
        <Col sm={6}>
          <Row>
            <PhoneNumber />
          </Row>
        </Col>
      </Row>
      <Subtitle py={3}>Carrier</Subtitle>
      <Row>
        <Col sm={6}>
          <Row>
            <MedicalInsuranceProvider onChange={() => dispatch(resetSection(CONFIRMATION_MODAL_FORM, "borMedicalGroupIDNumber"))} />
            {borMedicalInsuranceCompany === "other" && <OtherMedicalInsuranceProvider />}
          </Row>
        </Col>
        <Col sm={6}>
          <Row>
            <MedicalGroupIdNumber />
          </Row>
        </Col>
      </Row>
    </>
  );
};

const EditForm = withReduxForm(({ formOpen, setFormOpen, showSuccessMessage, handleSubmit }) => (
  <>
    <Div
      mt={2}
      py={1}
      className={classnames("fade", {
        in: !formOpen,
      })}
    >
      <Button
        type="button"
        style={{ maxWidth: "100%" }}
        px={0}
        color="link"
        onClick={(e) => {
          e.preventDefault();
          setFormOpen(true);
        }}
      >
        Edit Information in the Letter
      </Button>
      {showSuccessMessage && (
        <BodyText className={classnames("float-right", bem("successMessage"))}>
          <Icon icon="check-circle" size="xs" flavor="solid" /> Changes have been saved!
        </BodyText>
      )}
    </Div>
    <Collapse in={formOpen}>
      <form onSubmit={handleSubmit}>
        <BodyText>Changes made to the information below will automatically update in the letter after being saved.</BodyText>
        <Div mt={3} className="text-right">
          <Button color="link" onClick={() => setFormOpen(false)}>
            Cancel
          </Button>
          <Button type="submit" color="secondary">
            Save Changes
          </Button>
        </Div>
        <FormFields />
      </form>
    </Collapse>
  </>
));

const formInitialValues = createSelector(
  getBorApplication,
  ({
    borMedicalInsuranceCompany,
    borOtherMedicalInsuranceCompany,
    borMedicalGroupIDNumber,
    contactName,
    contactTitle,
    email,
    address1,
    city,
    state,
    zip,
    contactPhoneNumber,
  }) => ({
    borMedicalInsuranceCompany,
    borOtherMedicalInsuranceCompany,
    borMedicalGroupIDNumber,
    contactName,
    contactTitle,
    email,
    address1,
    city,
    state,
    zip,
    contactPhoneNumber,
  }),
);

const EditBrokerOfRecordLetterModal = ({ onAgree, onHide, ...rest }) => {
  const letterProps = useSelector(letterPreviewPropsFromBorApplication);
  const [formOpen, setFormOpen] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const initialValues = useSelector(formInitialValues);
  const [updateBorApplication] = useUpdateApplicationMutation();
  useEffect(() => {
    if (formOpen) {
      setShowSuccessMessage(false);
    }
  }, [formOpen]);

  return (
    <Modal onHide={onHide} {...rest} className={bem()}>
      <ModalHeader
        closeButton
        onHide={() =>
          AnalyticsTracking.trackEvent("cta_clicked", {
            label: "close_bor_letter_modal",
          })
        }
      >
        <Title>Review This Letter</Title>
      </ModalHeader>
      <ModalBody>
        <BodyText my={3}>
          {letterProps.carrier} will need to make a clerical change to update the broker of record for your current plan(s). Please review the letter that we
          will send to notify them of this update.
        </BodyText>
        <Div p={4} pb={0} className={bem("letter")}>
          <LetterContent {...letterProps} />
        </Div>
        <EditForm
          formOpen={formOpen}
          setFormOpen={setFormOpen}
          setShowSuccessMessage={setShowSuccessMessage}
          showSuccessMessage={showSuccessMessage}
          initialValues={initialValues}
          updateBorApplication={updateBorApplication}
        />

        <Collapse in={!formOpen}>
          <Row>
            <Col smOffset={9}>
              <Button
                fullWidth
                mt={4}
                disabeled={formOpen ? "disabled" : undefined}
                onClick={() => {
                  onAgree();
                  onHide();
                  AnalyticsTracking.trackEvent("cta_clicked", {
                    label: "clicked_ok_bor_letter_modal",
                  });
                }}
              >
                Ok
              </Button>
            </Col>
          </Row>
        </Collapse>
      </ModalBody>
    </Modal>
  );
};

export default EditBrokerOfRecordLetterModal;
